<script lang="ts">
import { defineComponent } from 'vue';
import securityService from '@/services/security';
import { TokenService } from '@/services/tokenService';

interface ErrorData {
  [key: string]: string;
}

export default defineComponent({
  name: 'BaseAuthForm',

  data() {
    return {
      serverError: '',
      rateLimitWarning: '',
      loading: false,
      cooldownActive: false,
      cooldownTimeRemaining: 0,
      cooldownTimer: null as number | null,
      errors: {} as ErrorData,
    };
  },

  computed: {
    formBlocked(): boolean {
      return this.cooldownActive || this.loading;
    },
  },

  methods: {
    /**
     * Start a cooldown timer for rate limiting
     */
    startCooldownTimer(): void {
      const cooldownInfo = securityService.getCooldownInfo();
      this.cooldownActive = true;
      this.cooldownTimeRemaining = Math.ceil(
        cooldownInfo.cooldownTimeMs / 1000
      );

      // Clear any existing timer
      if (this.cooldownTimer !== null) {
        window.clearInterval(this.cooldownTimer);
      }

      // Start countdown timer
      this.cooldownTimer = window.setInterval(() => {
        this.cooldownTimeRemaining--;
        if (this.cooldownTimeRemaining <= 0) {
          if (this.cooldownTimer !== null) {
            window.clearInterval(this.cooldownTimer);
          }
          this.cooldownActive = false;
          this.rateLimitWarning = '';
        }
      }, 1000);
    },

    /**
     * Update the rate limit warning message
     */
    updateRateLimitWarning(): void {
      const remainingAttempts = securityService.getRemainingLoginAttempts();
      if (remainingAttempts <= 2) {
        // Show warning when 2 or fewer attempts remain
        this.rateLimitWarning = `Preostalo ${remainingAttempts} pokušaja pre privremenog zaključavanja.`;
      } else {
        this.rateLimitWarning = '';
      }
    },

    /**
     * Handle API errors with standardized error handling
     * @param {Error} error - The error from the API call
     * @param {string} action - The action being performed (e.g., 'login', 'register')
     */
    handleApiError(error: any, action: string): void {
      console.error(`${action} error:`, error);

      if (error.response) {
        // Server returned an error
        const status = error.response.status;
        const data = error.response.data;

        if (status === 401) {
          this.serverError = 'Pogrešna email adresa ili lozinka';
        } else if (status === 422) {
          // Validation errors
          if (data.errors) {
            Object.keys(data.errors).forEach((key) => {
              if (this.errors && key in this.errors) {
                this.errors[key] = data.errors[key][0];
              }
            });
          } else {
            this.serverError =
              data.message || `Došlo je do greške prilikom validacije`;
          }
        } else if (status === 409) {
          // Conflict - already exists (for registration)
          if (data.message.includes('email')) {
            if (this.errors) this.errors.email = 'Email adresa je već zauzeta';
          } else if (data.message.includes('username')) {
            if (this.errors)
              this.errors.username = 'Korisničko ime je već zauzeto';
          } else {
            this.serverError = data.message || 'Korisnik već postoji';
          }
        } else if (status === 429) {
          // Rate limiting from server
          this.serverError = `Previše pokušaja ${
            action === 'login' ? 'prijave' : 'registracije'
          }. Pokušajte kasnije.`;
          this.startCooldownTimer();
        } else if (status === 403) {
          // Check if it's email verification issue
          if (
            data.message &&
            ((data.message.toLowerCase().includes('verify') &&
              data.message.toLowerCase().includes('email')) ||
              data.message.includes('Please verify your email address'))
          ) {
            this.serverError =
              'Molimo verifikujte vašu email adresu pre prijave.';

            // Optionally, you can emit an event to show a verification resend option
            this.$emit('email-verification-required', {
              message: 'Molimo verifikujte vašu email adresu pre prijave.',
            });
          } else {
            // CSRF token mismatch or other 403 errors
            this.serverError =
              'Bezbednosna greška. Osvežite stranicu i pokušajte ponovo.';
            securityService.refreshCsrfToken();
          }
        } else {
          this.serverError =
            data.message ||
            `Došlo je do greške prilikom ${
              action === 'login' ? 'prijave' : 'registracije'
            }`;
        }
      } else {
        this.serverError = 'Servis trenutno nije dostupan. Pokušajte kasnije.';
      }
    },

    /**
     * Process successful auth response (login or register)
     * @param {object} data - The response data from the server
     * @param {string} successEvent - The event to emit on success
     */
    processAuthResponse(data: any, successEvent: string): boolean {
      console.log('[BaseAuthForm] processAuthResponse called with:', {
        data,
        successEvent,
      });

      // Reset rate limiting on any successful response
      securityService.resetLoginAttempts();
      this.rateLimitWarning = '';

      // REGISTRATION: Always requires email verification (no tokens)
      if (successEvent === 'register-success') {
        console.log(
          '[BaseAuthForm] Registration successful - requires email verification'
        );

        this.$emit(successEvent, {
          requiresVerification: true,
          user: data.data?.user || null,
          message:
            data.data?.message ||
            data.message ||
            'Registracija uspešna. Proverite vaš email za verifikaciju.',
        });

        return true;
      }

      // LOGIN: Should have tokens
      if (successEvent === 'login-success') {
        if (data.data && data.data.tokens && data.data.tokens.accessToken) {
          console.log('[BaseAuthForm] Login successful with tokens');

          // Store access token
          TokenService.updateTokens(data.data);

          // Store refresh token if provided
          if (data.data.tokens.refreshToken) {
            localStorage.setItem('refreshToken', data.data.tokens.refreshToken);
          }

          // Store deviceId if provided
          if (data.data.tokens.deviceId) {
            localStorage.setItem('deviceId', data.data.tokens.deviceId);
          }

          // Store user data if provided
          if (data.data.user) {
            localStorage.setItem('user', JSON.stringify(data.data.user));
          }

          // Handle remember me functionality
          const formData = (this as any).formData;
          if (formData && formData.rememberMe) {
            localStorage.setItem(
              'email',
              securityService.sanitizeInput(formData.email)
            );
            localStorage.setItem('rememberMe', 'true');
          }

          // Emit success event
          this.$emit(successEvent, {
            requiresVerification: false,
            tokens: data.data.tokens,
            user: data.data.user,
          });

          // Check if there's a redirect path saved
          const redirectPath = localStorage.getItem('redirectAfterLogin');
          if (redirectPath) {
            localStorage.removeItem('redirectAfterLogin');
            this.$router.push(redirectPath);
          }

          return true;
        } else {
          console.error('[BaseAuthForm] Login response missing tokens');
          this.serverError =
            'Problem sa odgovorom servera. Nedostaju podaci za prijavu.';
          return false;
        }
      }

      // Fallback za nepoznate eventi
      console.warn('[BaseAuthForm] Unknown success event:', successEvent);
      this.$emit(successEvent, data);
      return true;
    },
  },

  beforeUnmount() {
    // Clean up timer on component destruction
    if (this.cooldownTimer !== null) {
      window.clearInterval(this.cooldownTimer);
    }
  },
});
</script>
